import {ChangeDetectionStrategy, Component, computed, ElementRef, Injectable, input, Signal, TemplateRef, viewChild} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {ReactiveFormsModule} from "@angular/forms";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {Deltakerstatus, EksternMøtedeltakerModell, MøtedeltakereModell, MøtedeltakerModell, MøteId, MøteModell, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatButtonModule} from "@angular/material/button";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {TranslateModule} from "@ngx-translate/core";
import {MøteDeltakereStatusKomponent} from "../møte-deltakere-status/møte-deltakere-status";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {Observable, switchMap} from "rxjs";
import {MøteDatatjeneste, MøteModellDLM} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {MøtedeltakereDatatjeneste} from "felles-ng2/tjenester/datalager/MøtedeltakereDatatjeneste";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Brukerkontekst} from "felles-ng2/tjenester/Brukerkontekst";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from "@angular/material/dialog";
import {SendMøteinnkallingDialogKomponent} from "web-ng2/moduler/møte/modale/send-møteinnkalling-dialog/send-møteinnkalling-dialog";
import {primærRolleSomStreng} from "felles-ng2/modell/medlemsRollePeriodeModellHjelpere";
import {tomMøteModell} from "felles-ng2/modell/tommeModeller";

const debug = initDebugger('MøteVisDeltakereKontroller');

@Component({
    selector: 'deltaker-i-liste',
    template: `
        <div class="deltaker e2e-deltaker e2e-deltaker-status-{{statusStrengTilE2eTester()}}">
            <avatar [medlem]="deltaker().medlem" [skjulTooltip]="true"></avatar>
            <div class="navnOgRolle">
                <div class="navn e2e_medlem_navn">{{ deltaker().medlem.navn }}</div>
                <div class="rolle">{{ aktivRolle() }}</div>
                @if (deltaker().aksjeeierRepresentasjoner.length > 0) {
                    <div class="representerer">
                        <div class="tittel">{{ 'Representerer aksjeeier' | translate }}</div>
                        <ul>
                            @for (aksjeeier of deltaker().aksjeeierRepresentasjoner; track aksjeeier.id) {
                                <li>{{ aksjeeier.navn }}</li>
                            }
                        </ul>
                    </div>
                }
            </div>
        </div>
    `,
    styleUrls: ['deltaker-i-liste.scss'],
    imports: [
        AvatarKomponent,
        TranslateModule
    ],
    standalone: true
})
export class DeltakerIListeKontroller {
    deltaker = input.required<MøtedeltakerModell>()

    aktivRolle = computed(() => {
        return primærRolleSomStreng(this.deltaker().aktiveMedlemsroller);
    })

    statusStrengTilE2eTester() {
        switch (this.deltaker().status) {
            case Deltakerstatus.akseptert:
                return "godtatt";
            case Deltakerstatus.avslaatt:
                return "avslaatt";
            case Deltakerstatus.innkalling_ikke_sendt:
                return "ikkesendt";
            case Deltakerstatus.innkalling_sendt:
                return "ikkesvart";
        }
    }
}

@Component({
    selector: 'ekstern-deltaker-i-liste',
    template: `
        <div class="deltaker">
            <avatar [navn]="navn()" [skjulTooltip]="true"></avatar>
            <div class="navnOgRolle">
                <div class="navn">{{ navn() }}</div>
                <div class="rolle">{{ aktivRolle() }}</div>
            </div>
        </div>
    `,
    styleUrls: ['deltaker-i-liste.scss'],
    imports: [
        AvatarKomponent
    ],
    standalone: true
})
export class EksternDeltakerIListeKontroller {
    deltaker = input.required<EksternMøtedeltakerModell>()

    aktivRolle = computed(() => this.deltaker().rolle)
    navn = computed(() => this.deltaker().navn);
}


@Component({
    selector: 'moete-vis-deltakere',
    templateUrl: './møte-vis-deltakere.html',
    styleUrls: ['./møte-vis-deltakere.scss'],
    imports: [
        ReactiveFormsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        MatSlideToggleModule,
        TranslateModule,
        MøteDeltakereStatusKomponent,
        DeltakerIListeKontroller,
        EksternDeltakerIListeKontroller,
        TipsKomponent,
        TittelVerdiKomponent,
        VerdiKomponent,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteVisDeltakereKontroller {
    static ajsSelector = 'moeteVisDeltakere';

    moeteId = input.required<MøteId>();

    moete: Signal<MøteModellDLM>;
    deltakere: Signal<MøtedeltakerModell[]>;
    deltakerinformasjon = computed(() => this.initDeltakerinformasjon());

    ikkeLaastOgSkriverettighet = computed<boolean>(() => {
          if (erNil(this.moete())) return false;
          if (this.moete()!.laastForRedigering) return false;
          if (this.avhengigheter.erIkkeAutentisert()) return false;
          if (this.avhengigheter.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.moete()!.eiendeGruppeId)) return true;
          return false;
      }
    );

    aktivBrukerKanGodtaEllerAvslaa = computed<boolean>(() => {
        if (erNil(this.moete())) return false;
        if (this.moete()!.laastForRedigering) return false;
        return this.deltakerinformasjon().aktivBrukerKanGodta || this.deltakerinformasjon().aktivBrukerKanAvslaa;
    });

    private sendPurringDialogTemplate = viewChild.required<TemplateRef<ElementRef>>('sendPurringTemplate');

    protected readonly Deltakerstatus = Deltakerstatus;
    private sendPurringDialog: MatDialogRef<any> | null = null;

    constructor(private avhengigheter: MøteVisDeltakereKontrollerAvhengigheter, private matDialog: MatDialog) {
        let møteIdObserverbar = toObservable(this.moeteId);
        this.moete = toSignal(møteIdObserverbar.pipe(switchMap(id => this.avhengigheter.hentMøteFraLager(id))), {initialValue: tomMøteModell()})
        this.deltakere = toSignal(møteIdObserverbar.pipe(switchMap(id => this.avhengigheter.hentDeltakereFraLager(id))), {initialValue: []})
    }

    sendInnkalling() {
        if (!this.ikkeLaastOgSkriverettighet()) return;
        if (erNil(this.moete())) return;
        let forhåndsvalgteMottakere = this.deltakerinformasjon().ikkeSendt.map(deltaker => deltaker.medlem.id);
        SendMøteinnkallingDialogKomponent.vis(this.matDialog, Promise.resolve(this.moete()), Promise.resolve(this.deltakere()), forhåndsvalgteMottakere);
    }

    sendPurring() {
        this.sendPurringDialog = this.matDialog.open(this.sendPurringDialogTemplate());
    }

    avbrytSendPurring() {
        this.sendPurringDialog!.close();
    }

    sendPurringIDialog() {
        this.sendPurringDialog!.close();
        this.avhengigheter.sendPurring(this.moeteId());
    }

    godtaInnkalling() {
        this.avhengigheter.godtaInnkalling(this.moeteId());
    }

    avslaaInnkalling() {
        this.avhengigheter.avslaaInnkalling(this.moeteId());
    }

    private initDeltakerinformasjon(): Deltakerinformasjon {
        let deltaker = this.finnInnloggetDeltaker();
        let godtatt = this.deltakere().filter(deltaker => deltaker.status == Deltakerstatus.akseptert);
        let avslått = this.deltakere().filter(deltaker => deltaker.status == Deltakerstatus.avslaatt);
        let ikkeSvart = this.deltakere().filter(deltaker => deltaker.status == Deltakerstatus.innkalling_sendt);
        let ikkeSendt = this.deltakere().filter(deltaker => deltaker.status == Deltakerstatus.innkalling_ikke_sendt);

        return {
            aktivBrukerInvitert: deltaker ? true : false,
            aktivBrukersStatus: deltaker ? deltaker.status : null,
            aktivBrukerKanBesvareInvitasjon: deltaker ? ![Deltakerstatus.akseptert, Deltakerstatus.avslaatt, Deltakerstatus.innkalling_ikke_sendt].includes(deltaker.status) : false,
            aktivBrukerKanGodta: deltaker ? [Deltakerstatus.avslaatt, Deltakerstatus.innkalling_sendt].includes(deltaker.status) : false,
            aktivBrukerKanAvslaa: deltaker ? [Deltakerstatus.akseptert, Deltakerstatus.innkalling_sendt].includes(deltaker.status) : false,
            antallInvitert: this.deltakere().length,
            godtatt: godtatt,
            avslaatt: avslått,
            ikkeSvart: ikkeSvart,
            ikkeSendt: ikkeSendt,
            eksterne: this.moete()!.eksterneDeltakere
        }
    }

    private tomDeltakerinformasjon(): Deltakerinformasjon {
        return {
            aktivBrukerInvitert: false,
            aktivBrukersStatus: null,
            aktivBrukerKanBesvareInvitasjon: false,
            aktivBrukerKanGodta: false,
            aktivBrukerKanAvslaa: false,
            antallInvitert: 0,
            godtatt: [],
            avslaatt: [],
            ikkeSvart: [],
            ikkeSendt: [],
            eksterne: []
        }
    }

    private finnInnloggetDeltaker(): MøtedeltakerModell | null {
        // Har fått endel feilmeldinger på denne når brukere er logget ut ettersom den kalles i en computed()
        if (this.avhengigheter.erIkkeAutentisert()) return null;
        let brukerId = this.avhengigheter.autentisertBruker().bruker.id;
        return this.deltakere().find((deltaker) => deltaker.medlem.brukerId === brukerId) ?? null;
    }
}

@Injectable()
export class MøteVisDeltakereKontrollerAvhengigheter {


    constructor(private møteDatatjeneste: MøteDatatjeneste,
                private møtedeltakereDatatjeneste: MøtedeltakereDatatjeneste,
                private brukerkontekstTilbyder: BrukerkontekstTilbyder) {
    }

    autentisertBruker(): Brukerkontekst {
        return this.brukerkontekstTilbyder.autentisertBruker();
    }

    erIkkeAutentisert(): boolean {
        return this.brukerkontekstTilbyder.erIkkeAutentisert();
    }

    hentMøteFraLager(id: MøteId): Observable<MøteModell> {
        return this.møteDatatjeneste.hentFraFraLokallagerSomObservable(id);
    }

    hentDeltakereFraLager(id: MøteId): Observable<MøtedeltakerModell[]> {
        return this.møtedeltakereDatatjeneste.finnTilknyttetMøteFraLokallager(id);
    }

    async sendPurring(id: MøteId): Promise<MøtedeltakereModell> {
        return await this.møteDatatjeneste.sendPurring(id);
    }

    godtaInnkalling(møteId: MøteId) {
        this.møteDatatjeneste.godtaInnkalling(møteId);
    }

    avslaaInnkalling(møteId: MøteId) {
        this.møteDatatjeneste.avslåInnkalling(møteId);
    }

}

interface Deltakerinformasjon {
    aktivBrukerInvitert: boolean;
    aktivBrukersStatus: Deltakerstatus | null;
    aktivBrukerKanBesvareInvitasjon: boolean;
    aktivBrukerKanGodta: boolean
    aktivBrukerKanAvslaa: boolean
    antallInvitert: number;
    godtatt: MøtedeltakerModell[];
    avslaatt: MøtedeltakerModell[];
    ikkeSvart: MøtedeltakerModell[];
    ikkeSendt: MøtedeltakerModell[];
    eksterne: EksternMøtedeltakerModell[];
}

